import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { useGetPhonicsSoundsQuery } from '@/api/types';
import { SymbolType, CdnObjectType } from '@/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { CdnMedia } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Spell } from '../components/Spell';
import { Activity, ActivityStep, ActivityStepProps } from '../Activity';
import { ActivityTypeProps } from '../types';
import { getStepNumber } from '../utils';

interface ReadingReadWordsStepProps extends ActivityStepProps {
  index: number;
  word: string;
}

const ReadingReadWordsStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  stepNumber,
  totalSteps,
  index,
  word,
}: ReadingReadWordsStepProps) => {
  const { enablePlayButton, disablePlayButton } = useLessonPlayer();

  const spellRef = useRef<Spell>(null);

  const [imageShowing, setImageShowing] = useState<boolean>(false);

  const [playCount, setPlayCount] = useState(0);

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  return (
    <ActivityStep
      key={`${activity.id}-${index}`}
      stepIdentifier={`${activity.id}-${index}`}
      activity={activity}
      mode={mode}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={`Say the sounds and blend them to make words.`}
      showTitle={true}
      showActivityPagination={false}
      onSlide={() => {
        enablePlayButton({
          onClick: () => {
            console.log('play', playCount);
            spellRef.current?.play();
          },
          hint: playCount < 2,
        });
      }}
      onBeforeLeave={() => {
        disablePlayButton();

        return true;
      }}
    >
      <WhitePanel mode="basic" kind="fill" color="paper" gap={true}>
        <CdnMedia
          className={clsx(
            'block mx-auto w-auto h-0 bg-white rounded-xl',
            'opacity-0 transition-all duration-300 ease-in-out',
            imageShowing && 'h-72 opacity-100',
          )}
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <Spell
          ref={spellRef}
          input={word}
          sounds={soundsData?.getPhonicsSounds.map((sound) => sound.name) || []}
          muteAudio={playCount < 1}
          onEnded={() =>
            setTimeout(() => {
              setPlayCount(playCount + 1);
              setImageShowing(playCount > 0);
            }, 750)
          }
        />
      </WhitePanel>
    </ActivityStep>
  );
};

export const ReadingReadWords = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words || !activity.words2) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  const STEPS_PER_WORD = 1;
  const TOTAL_STEPS = words.length * STEPS_PER_WORD + 1;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          parrotType="tablet-chair"
          isPreview={isPreview}
          index={0}
          total={words.length + 1}
        />,
        ...words.map((word, index) => (
          <>
            <ReadingReadWordsStep
              key={`${activity.id}-${index}-1`}
              stepIdentifier={`${activity.id}-${index}-1`}
              activity={activity}
              mode={mode}
              wrapperClassName={wrapperClassName}
              isPreview={isPreview}
              stepNumber={getStepNumber(index + 1, 0, STEPS_PER_WORD)}
              totalSteps={TOTAL_STEPS}
              index={index + 1}
              word={word}
            />
          </>
        )),
      ]}
    />
  );
};
